/**
 * Delegation: who is carrying which task or check (owner, 2026-10-01: "What's the delegating process of tasks to
 * employees?"). A pure core, so the API, the web demo and the phone demo agree and every rule is tested at a pinned clock.
 *
 * Today, a task goes to whoever is on shift and has no named owner. This adds one, and keeps the old behavior as the default.
 *
 * | Rule | Why |
 * |---|---|
 * | A1 Unassigned = anyone on shift, exactly as before. An assignment only ever ADDS a name | Nothing that worked stops working; a place that never assigns sees no change |
 * | A2 Who may assign: a manager, admin or owner, or a staff member who is the person in charge. Other staff may step in (A6), never assign | The same rule as "Hand off the shift" and Nag (handoffs.ts, nags.ts): authority follows the role in charge |
 * | A3 A target is a person ("Susan") or a role on shift: "the opener", "whoever closes", "any barista on shift" (title:barista). A role resolves to the PEOPLE ON SHIFT it fits (resolveTarget). Nobody on shift fits: it stays assigned, shows "nobody on shift is a barista", and anyone on shift can still do it | The model has no shift times, so a role is narrowed only where the data can tell (a title; the first check-in of the day for the opener). "Whoever closes" is every person on shift: it cannot be narrowed honestly, so the first to say "I'll do it" takes it (A6) |
 * | A4 Two scopes: "today" is this one occurrence on this day; "standing" is every day (the check's base key, "close-cold" for every "close-cold@22:00"). Today's choice beats a standing rule; "remove today" frees one day without ending the rule | "Every close" is a rule; "just this one" is a favor. The person picks, in words |
 * | A5 The assignee sees it first: in their Next and deck, with "Dana asked you". Everyone else still sees it as "Susan's". Food-safety order (next.ts N1) is never overturned by an assignment | A late fridge check still insists for everybody; delegation orders the rest |
 * | A6 Anyone on shift can step in: "I'll do it". It is recorded (who stepped in, for whom) and the item becomes theirs for the day | Work gets done by whoever is there, and the record says so. Never a reprimand for the person it was assigned to (G2b) |
 * | A7 Late is "needs a hand", never a name on a list of shame. Each row offers Reassign or Help. People are listed in a fixed order (on shift first, then A to Z), never by how much they did or missed | G2b: kindness in truth. The truth (it is late) is stated; the person is not ranked |
 * | A8 Every assignment, reassignment, removal and step-in is an event in an append-only list, and each also goes on the audit trail | The binder can show who was asked and who did it |
 * | A9 Words never carry health information (FRAMEWORK T4). A label with a health word is replaced by "a task" | The same scan as push.ts |
 */
import { firstName } from "./actions.ts";
import type { ExpectedCheck } from "./schedule.ts";
import { isLate } from "./schedule.ts";
import { hasHealthWords } from "./push.ts";
import type { Id, IsoDate, IsoDateTime, Role } from "./model.ts";
import { availablePeople, suggestAssignees, type SuggestHistory, type Suggestion } from "./assignsuggest.ts";

/* ─────────────── model ─────────────── */

export type AssignScope = "today" | "standing";
export type AssignAction = "assign" | "step_in" | "remove";
/** How the event arrived: a person's tap, or a hand-off to the person in charge (handoffs.ts). */
export type AssignVia = "assign" | "handoff" | "step_in";

/** One event in the append-only list (migration 0062 task_assignments). The current owner of an item is the fold of its events (currentAssignment). */
export interface TaskAssignment {
  id: Id;
  orgId: Id;
  locationId: Id;
  /** Today: the full check key ("close-cold@22:00"). Standing: its base key ("close-cold"). */
  itemKey: string;
  itemTitle: string;
  action: AssignAction;
  scope: AssignScope;
  /** The place's local day. Today's events only. */
  date?: IsoDate;
  /** For assign: a person or a role. For step_in: always the person who stepped in. */
  targetKind?: "person" | "role";
  employeeId?: Id;
  role?: string;
  /** Who asked (assign, remove) or who stepped in (step_in). */
  byEmployeeId?: Id;
  byName: string;
  /** The person it was assigned to before a step-in, so the record says "for Susan". */
  forEmployeeId?: Id;
  via: AssignVia;
  at: IsoDateTime;
}

export type AssignTarget = { kind: "person"; employeeId: Id } | { kind: "role"; role: string };

/** A person at this place, as delegation sees them. */
export interface ShiftPerson {
  id: Id;
  name: string;
  /** Their job title ("Barista"); matched for "any barista on shift". */
  title?: string;
  isPersonInCharge: boolean;
  onShift: boolean;
  /** When they checked in today (the opener is the earliest). */
  checkedInAt?: IsoDateTime;
  role?: Role;
  /** Days they usually work, 0 = Sunday … 6 = Saturday (Employee.workDays): the schedule, for suggestions. */
  workDays?: number[];
  /** Their last day, when they have one (Employee.endsOn). Past it, they are not offered. */
  endsOn?: IsoDate;
}

/* ─────────────── keys ─────────────── */

/** "close-cold@22:00" is one occurrence of "close-cold". Task keys ("task-<id>@09:00") work the same way. */
export const baseKey = (key: string): string => key.replace(/@\d{2}:\d{2}$/, "");

/* ─────────────── roles on shift (A3) ─────────────── */

export const OPENER = "opener";
export const CLOSER = "closer";
export const TITLE_ROLE = "title:";

/** The titles that read as the same job ("Barista", "barista", "Lead barista"): the word a role matches. */
export const titleRoleOf = (title: string): string => `${TITLE_ROLE}${title.trim().toLowerCase()}`;

/** The words for a role, as the screen and the notice say them. `any barista on shift`, `the opener`, `whoever closes`. */
export function roleLabel(role: string): { label: string; line: string } {
  if (role === OPENER) return { label: "The opener", line: "the opener" };
  if (role === CLOSER) return { label: "Whoever closes", line: "whoever closes" };
  if (role.startsWith(TITLE_ROLE)) {
    const t = role.slice(TITLE_ROLE.length).trim();
    return { label: `Any ${t} on shift`, line: `any ${t} on shift` };
  }
  return { label: "Someone on shift", line: "someone on shift" };
}

/** The role choices a manager is offered: the opener, whoever closes, and one "any <title> on shift" per job title among the people here. */
export function roleChoices(people: readonly Pick<ShiftPerson, "title">[]): { role: string; label: string }[] {
  const titles = [...new Set(people.map((p) => (p.title ?? "").trim().toLowerCase()).filter((t) => t && !/person in charge|owner|manager|volunteer/.test(t)))].sort();
  return [OPENER, CLOSER, ...titles.map(titleRoleOf)].map((role) => ({ role, label: roleLabel(role).label }));
}

/** How close to the first check-in still counts as opening together. A judgment default (ASSUMED), not a legal number. */
export const OPENER_WINDOW_MIN = 15;

/**
 * A3: the people a target means right now. A person: that person (on shift or not: it is theirs for when they come).
 * A role: the people ON SHIFT it fits. The opener: whoever checked in first today (all who checked in within
 * OPENER_WINDOW_MIN of the first), or everyone on shift when nobody has checked in. Whoever closes: everyone on shift
 * (no data narrows it). A job title: the people on shift with it.
 */
export function resolveTarget(t: AssignTarget, people: readonly ShiftPerson[]): ShiftPerson[] {
  if (t.kind === "person") return people.filter((p) => p.id === t.employeeId);
  const on = people.filter((p) => p.onShift);
  if (t.role === OPENER) {
    const stamped = on.filter((p) => p.checkedInAt).sort((a, b) => a.checkedInAt!.localeCompare(b.checkedInAt!));
    if (!stamped.length) return on;
    const first = Date.parse(stamped[0]!.checkedInAt!);
    return stamped.filter((p) => Date.parse(p.checkedInAt!) - first <= OPENER_WINDOW_MIN * 60_000);
  }
  if (t.role === CLOSER) return on;
  if (t.role.startsWith(TITLE_ROLE)) {
    const want = t.role.slice(TITLE_ROLE.length).trim().toLowerCase();
    return want ? on.filter((p) => (p.title ?? "").toLowerCase().includes(want)) : [];
  }
  return [];
}

/* ─────────────── who may assign (A2) ─────────────── */

/** A2: a manager, admin or owner, or a staff member who is the person in charge. */
export function mayAssign(role: Role, isPersonInCharge: boolean): boolean {
  return role !== "staff" || isPersonInCharge;
}

export const ASSIGN_DENIED = "Only a manager or the person in charge can give out tasks. You can still say \"I'll do it\" on one.";

/* ─────────────── the current owner of an item (A4) ─────────────── */

export interface CurrentAssignment {
  /** The event that decides it. */
  event: TaskAssignment;
  scope: AssignScope;
  target: AssignTarget;
  byName: string;
  byEmployeeId?: Id;
  /** Set when the last word was a step-in: who stepped in, and (when known) for whom. */
  steppedIn?: { employeeId: Id; name: string; forEmployeeId?: Id };
}

const newest = (a: TaskAssignment, b: TaskAssignment) => b.at.localeCompare(a.at) || b.id.localeCompare(a.id);

/**
 * The time to write on a new event for `itemKey`: now, but never at or before an earlier event on the same task. Events are
 * ordered by time and two taps in the same millisecond (a pinned test clock, a double tap) must still keep their order, so
 * the later one is one millisecond after the earlier (ids are random within a millisecond and cannot settle it).
 */
export function nextAt(events: readonly Pick<TaskAssignment, "itemKey" | "at">[], itemKey: string, now: IsoDateTime): IsoDateTime {
  const base = baseKey(itemKey);
  const last = events.filter((e) => baseKey(e.itemKey) === base).map((e) => e.at).sort().at(-1);
  return last && last >= now ? new Date(Date.parse(last) + 1).toISOString() : now;
}

function currentOf(e: TaskAssignment): CurrentAssignment | undefined {
  if (e.action === "remove") return undefined;
  if (e.action === "step_in") {
    if (!e.employeeId) return undefined;
    return { event: e, scope: e.scope, target: { kind: "person", employeeId: e.employeeId }, byName: e.byName, ...(e.byEmployeeId ? { byEmployeeId: e.byEmployeeId } : {}), steppedIn: { employeeId: e.employeeId, name: e.byName, ...(e.forEmployeeId ? { forEmployeeId: e.forEmployeeId } : {}) } };
  }
  const target: AssignTarget | undefined = e.targetKind === "person" && e.employeeId ? { kind: "person", employeeId: e.employeeId } : e.targetKind === "role" && e.role ? { kind: "role", role: e.role } : undefined;
  if (!target) return undefined;
  return { event: e, scope: e.scope, target, byName: e.byName, ...(e.byEmployeeId ? { byEmployeeId: e.byEmployeeId } : {}) };
}

/**
 * A4: who owns `itemKey` on `date`. Today's newest event decides when there is one (a "remove" frees this day only); else the
 * newest standing event for the item's base key (a standing "remove" ends the rule); else nobody (A1).
 */
export function currentAssignment(events: readonly TaskAssignment[], itemKey: string, date: IsoDate): CurrentAssignment | undefined {
  const today = events.filter((e) => e.scope === "today" && e.date === date && e.itemKey === itemKey).sort(newest);
  if (today.length) return currentOf(today[0]!);
  const standing = events.filter((e) => e.scope === "standing" && e.itemKey === baseKey(itemKey)).sort(newest);
  // A step-in stands for one day only, so it is never a standing event; a standing step-in row can't exist (assignEvent refuses it).
  return standing.length ? currentOf(standing[0]!) : undefined;
}

/* ─────────────── the events a request makes ─────────────── */

export type AssignRequest =
  | { itemKey: string; itemTitle: string; to: AssignTarget; scope: AssignScope }
  | { itemKey: string; itemTitle: string; to: null; scope: AssignScope };

export interface AssignActor { employeeId?: Id; name: string; role: Role; isPersonInCharge: boolean }

export type AssignResult = { ok: true; event: Omit<TaskAssignment, "id" | "orgId" | "locationId"> } | { ok: false; status: number; error: string };

/** A2, A4, A8: the event an assign (or remove, `to: null`) request makes, or why not. `known` are the employees at this place. */
export function assignEvent(req: AssignRequest, who: AssignActor, known: readonly Pick<ShiftPerson, "id" | "name">[], now: IsoDateTime, date: IsoDate, via: AssignVia = "assign"): AssignResult {
  if (!mayAssign(who.role, who.isPersonInCharge)) return { ok: false, status: 403, error: ASSIGN_DENIED };
  if (req.scope !== "today" && req.scope !== "standing") return { ok: false, status: 400, error: '"scope" must be today or standing.' };
  if (!req.itemKey || req.itemKey.length > 200) return { ok: false, status: 400, error: "Say which task." };
  const key = req.scope === "standing" ? baseKey(req.itemKey) : req.itemKey;
  const base = { itemKey: key, itemTitle: req.itemTitle.slice(0, 200), scope: req.scope, ...(req.scope === "today" ? { date } : {}), byName: who.name, ...(who.employeeId ? { byEmployeeId: who.employeeId } : {}), via, at: now };
  if (req.to === null) return { ok: true, event: { ...base, action: "remove" } };
  if (req.to.kind === "person") {
    const p = known.find((x) => x.id === (req.to as { employeeId: Id }).employeeId);
    if (!p) return { ok: false, status: 400, error: "That person doesn't work at this place." };
    return { ok: true, event: { ...base, action: "assign", targetKind: "person", employeeId: p.id } };
  }
  if (!roleLabel(req.to.role).label || !(req.to.role === OPENER || req.to.role === CLOSER || req.to.role.startsWith(TITLE_ROLE))) return { ok: false, status: 400, error: "Pick the opener, whoever closes, or a job on shift." };
  return { ok: true, event: { ...base, action: "assign", targetKind: "role", role: req.to.role } };
}

/** A6: anyone on shift steps in on an occurrence, for today only. `forEmployeeId` is who it was with. */
export function stepInEvent(i: { itemKey: string; itemTitle: string; by: { employeeId: Id; name: string }; current?: CurrentAssignment; people: readonly ShiftPerson[] }, now: IsoDateTime, date: IsoDate): AssignResult {
  const cur = i.current;
  // Already theirs: nothing to record.
  if (cur?.target.kind === "person" && cur.target.employeeId === i.by.employeeId) return { ok: false, status: 409, error: "It's already yours." };
  const forId = cur?.target.kind === "person" ? cur.target.employeeId : undefined;
  return { ok: true, event: { itemKey: i.itemKey, itemTitle: i.itemTitle.slice(0, 200), action: "step_in", scope: "today", date, targetKind: "person", employeeId: i.by.employeeId, byEmployeeId: i.by.employeeId, byName: i.by.name, ...(forId ? { forEmployeeId: forId } : {}), via: "step_in", at: now } };
}

/* ─────────────── what a viewer sees on one item (A5) ─────────────── */

/** One item's assignment as one viewer reads it (GET /today `assign[key]`). Names are first names. */
export interface AssignView {
  scope: AssignScope;
  /** "Dana" */
  by: string;
  byMe: boolean;
  target: { kind: "person"; employeeId: Id; name: string } | { kind: "role"; role: string; label: string; line: string };
  /** Who it is, resolved right now (a role: the people on shift it fits). */
  to: { employeeId: Id; name: string }[];
  /** It is mine: named, or my role resolves to me. */
  mine: boolean;
  steppedIn?: { name: string; for?: string };
  /** The one line on the card: "Dana asked you." / "Susan's." */
  line: string;
  /** For the person who gave it (or a manager), never the person it was given to: "Seen by Sam 2:14 PM" / "Not seen yet, emailed 2:30 PM" (push.ts P12). */
  seen?: string;
}

/** P12, for the assigner: has the person seen the task, and if not, did the email go? */
export function assignSeenLine(name: string, o: { seenAt?: string; emailedAt?: string }, timeLabel: (iso: string) => string): string {
  if (o.seenAt) return `Seen by ${firstName(name)} ${timeLabel(o.seenAt)}`;
  return o.emailedAt ? `Not seen yet, emailed ${timeLabel(o.emailedAt)}` : "Not seen yet";
}

/** The line on a card (A5). Mine: who asked. Someone else's: whose it is, and a step-in when one happened. */
export function assignLine(a: Pick<AssignView, "by" | "byMe" | "target" | "to" | "mine" | "steppedIn">): string {
  if (a.steppedIn) {
    return a.mine ? (a.steppedIn.for ? `You stepped in for ${a.steppedIn.for}.` : "You stepped in.") : `${a.steppedIn.name} stepped in${a.steppedIn.for ? ` for ${a.steppedIn.for}` : ""}.`;
  }
  if (a.target.kind === "role") {
    const asker = a.byMe ? "You asked" : `${a.by} asked`;
    return a.mine ? `${asker} ${a.target.line}.` : `${asker} ${a.target.line}.`;
  }
  if (a.mine) return a.byMe ? "You took this one." : `${a.by} asked you.`;
  return `${a.target.name}'s.`;
}

/** The item's assignment as `viewerId` reads it, or undefined when nobody is named (A1). */
export function assignViewOf(cur: CurrentAssignment | undefined, people: readonly ShiftPerson[], viewerId: Id | undefined): AssignView | undefined {
  if (!cur) return undefined;
  const nameOf = (id: Id) => people.find((p) => p.id === id)?.name;
  const resolved = resolveTarget(cur.target, people);
  const to = resolved.map((p) => ({ employeeId: p.id, name: firstName(p.name) }));
  const byMe = !!viewerId && cur.byEmployeeId === viewerId;
  const base = { scope: cur.scope, by: firstName(cur.byName), byMe, to, mine: !!viewerId && resolved.some((p) => p.id === viewerId) };
  let target: AssignView["target"];
  if (cur.target.kind === "person") {
    const n = nameOf(cur.target.employeeId);
    if (!n) return undefined; // a person who no longer works here: back to anyone (A1)
    target = { kind: "person", employeeId: cur.target.employeeId, name: firstName(n) };
  } else {
    const l = roleLabel(cur.target.role);
    target = { kind: "role", role: cur.target.role, label: l.label, line: l.line };
  }
  const forName = cur.steppedIn?.forEmployeeId ? nameOf(cur.steppedIn.forEmployeeId) : undefined;
  const steppedIn = cur.steppedIn ? { name: firstName(cur.steppedIn.name), ...(forName ? { for: firstName(forName) } : {}) } : undefined;
  const v: Omit<AssignView, "line"> = { ...base, target, ...(steppedIn ? { steppedIn } : {}) };
  return { ...v, line: assignLine(v) };
}

/* ─────────────── "Who's carrying what" (A7) ─────────────── */

export type CarryState = "done" | "due" | "late";

export interface CarryItem {
  key: string;
  title: string;
  dueAt: IsoDateTime;
  state: CarryState;
  /** "Done" / "Due 2:00 PM" / "Needs a hand" (A7: late is never a name on a list). */
  stateLine: string;
  scope: AssignScope;
  /** Who asked ("Dana"). */
  by?: string;
  /** Set when a step-in decided it: "Sam stepped in for Susan." */
  note?: string;
}

export interface CarryPerson {
  employeeId: Id;
  name: string;
  onShift: boolean;
  isMe: boolean;
  items: CarryItem[];
  counts: { done: number; due: number; late: number };
  /** "2 done · 1 due · 1 needs a hand", or "Nothing assigned." */
  line: string;
}

/** A role's items: who it resolves to now. */
export interface CarryRole { role: string; label: string; people: string[]; items: CarryItem[]; line: string }

export interface CarryView {
  date: IsoDate;
  /** Everyone on shift first, then others holding an assignment; each in a fixed order (A7), never ranked. */
  people: CarryPerson[];
  roles: CarryRole[];
  /** Open items nobody is named on: anyone on shift can do them (A1). Done ones are left out. */
  unassigned: CarryItem[];
  /** Who and what a manager can pick in the Assign sheet. */
  choices: {
    people: { employeeId: Id; name: string; onShift: boolean; /** Scheduled today (work days). */ scheduled: boolean }[];
    roles: { role: string; label: string }[];
    /** Up to 3 people to offer in the Assign control for each open item, by its key, each with why (assignsuggest.ts). Present when the place's history was given. */
    suggest?: Record<string, Suggestion[]>;
  };
  /** One line for the card on Today: "3 tasks given out · 1 needs a hand". */
  line: string;
  counts: { assigned: number; done: number; due: number; late: number };
}

export interface CarryInput {
  now: IsoDateTime;
  date: IsoDate;
  people: readonly ShiftPerson[];
  expected: readonly (Pick<ExpectedCheck, "key" | "label" | "dueAt" | "lateAt" | "firstDay"> & { satisfied?: boolean })[];
  events: readonly TaskAssignment[];
  meId?: Id;
  timeLabel: (iso: IsoDateTime) => string;
  /** Check-in presence, done records, hours and zone: what the suggestions learn from (assignsuggest.ts). Never health answers. */
  history?: SuggestHistory;
}

export const NEEDS_A_HAND = "Needs a hand";

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

export function carryLine(c: { done: number; due: number; late: number }): string {
  const parts = [c.done ? `${c.done} done` : "", c.due ? `${c.due} due` : "", c.late ? `${c.late} needs a hand` : ""].filter(Boolean);
  return parts.length ? parts.join(" · ") : "Nothing assigned.";
}

/** A7. Pure: the same inputs give the same view. */
export function carryingView(i: CarryInput): CarryView {
  const byId = new Map(i.people.map((p) => [p.id, p]));
  const personRows = new Map<Id, CarryItem[]>();
  const roleRows = new Map<string, CarryItem[]>();
  const unassigned: CarryItem[] = [];
  for (const e of i.expected) {
    const cur = currentAssignment(i.events, e.key, i.date);
    const state: CarryState = e.satisfied ? "done" : isLate(e, i.now) ? "late" : "due";
    const view = assignViewOf(cur, i.people, i.meId);
    const item: CarryItem = {
      key: e.key, title: e.label.replace(/[.\s]+$/, ""), dueAt: e.dueAt, state, scope: cur?.scope ?? "today",
      stateLine: state === "done" ? "Done" : state === "late" ? NEEDS_A_HAND : `Due ${i.timeLabel(e.dueAt)}`,
      ...(view ? { by: view.by } : {}), ...(view?.steppedIn ? { note: view.line } : {}),
    };
    if (!cur || !view) { if (state !== "done") unassigned.push(item); continue; }
    if (cur.target.kind === "person") personRows.set(cur.target.employeeId, [...(personRows.get(cur.target.employeeId) ?? []), item]);
    else roleRows.set(cur.target.role, [...(roleRows.get(cur.target.role) ?? []), item]);
  }
  const countOf = (items: CarryItem[]) => ({ done: items.filter((x) => x.state === "done").length, due: items.filter((x) => x.state === "due").length, late: items.filter((x) => x.state === "late").length });
  const byDue = (a: CarryItem, b: CarryItem) => a.dueAt.localeCompare(b.dueAt) || a.key.localeCompare(b.key);
  // A7: on shift first, then A to Z. Never by what a person did or missed.
  const order = (a: ShiftPerson, b: ShiftPerson) => Number(b.onShift) - Number(a.onShift) || a.name.localeCompare(b.name);
  const rows = [...i.people].filter((p) => p.onShift || personRows.has(p.id)).sort(order).map((p): CarryPerson => {
    const items = (personRows.get(p.id) ?? []).sort(byDue);
    const counts = countOf(items);
    return { employeeId: p.id, name: p.name, onShift: p.onShift, isMe: p.id === i.meId, items, counts, line: carryLine(counts) };
  });
  const roles = [...roleRows].sort(([a], [b]) => a.localeCompare(b)).map(([role, items]): CarryRole => {
    const names = resolveTarget({ kind: "role", role }, i.people).map((p) => firstName(p.name));
    const l = roleLabel(role);
    const sorted = items.sort(byDue);
    return { role, label: l.label, people: names, items: sorted, line: names.length ? `On shift: ${names.join(", ")}` : `Nobody on shift fits ${l.line}. Anyone on shift can do it.` };
  });
  const all = [...rows.flatMap((r) => r.items), ...roles.flatMap((r) => r.items)];
  const total = countOf(all);
  const assigned = all.length;
  return {
    date: i.date, people: rows, roles, unassigned: unassigned.sort(byDue),
    choices: { people: availablePeople(i.people, i.date).map((p) => ({ employeeId: p.id, name: p.name, onShift: p.onShift, scheduled: p.scheduled })), roles: roleChoices(i.people),
      ...(i.history ? { suggest: Object.fromEntries(i.expected.filter((e) => !e.satisfied).map((e) => [e.key, suggestAssignees({ itemKey: e.key, dueAt: e.dueAt, date: i.date, people: i.people, events: i.events, history: i.history!, ...(i.meId ? { meId: i.meId } : {}) })])) } : {}) },
    line: assigned ? `${plural(assigned, "task", "tasks")} given out${total.late ? ` · ${total.late} ${total.late === 1 ? "needs" : "need"} a hand` : ""}` : "Nothing given out yet.",
    counts: { assigned, ...total },
  };
}

/* ─────────────── words (A9) ─────────────── */

/** A9: a task's label as a notice may carry it. A health word anywhere: no label at all. */
export function safeTaskLabel(label: string): string | undefined {
  const t = label.replace(/\s+/g, " ").trim().replace(/[.:]+$/, "");
  return !t || hasHealthWords(t) ? undefined : t.length > 60 ? `${t.slice(0, 59).trimEnd()}…` : t;
}

/** The in-app notice to the person asked (a ShiftNotice, kind "assign"; refId "check:<key>" opens the task like a Nag does). */
export function assignedNoticeText(o: { byName: string; itemTitle: string; target: AssignTarget; role?: string; scope: AssignScope }): { title: string; body: string } {
  const by = firstName(o.byName);
  const label = safeTaskLabel(o.itemTitle);
  const whom = o.target.kind === "role" ? ` ${roleLabel(o.target.role).line}` : "";
  const title = label ? `${by} asked${o.target.kind === "role" ? whom : " you"}: ${label}` : `${by} asked${o.target.kind === "role" ? whom : " you"} to do a task`;
  const body = o.scope === "standing" ? "It's yours every time now. It shows first on Today." : "It's on your list for today, first.";
  return { title, body };
}

/** The in-app notice to the person the task was with, when someone else steps in. Kind, never blame (G2b). */
export function stepInNoticeText(o: { byName: string; itemTitle: string }): { title: string; body: string } {
  const label = safeTaskLabel(o.itemTitle);
  return { title: `${firstName(o.byName)} is doing ${label ? `"${label}"` : "a task"} for you`, body: "Nothing to do. It's covered. Thank them." };
}

/** What the manager's Reassign/Help buttons say, shared by web and phone. */
export const DELEGATE_WORDS = {
  assign: "Give it to someone",
  reassign: "Reassign",
  help: "Help",
  stepIn: "I'll do it",
  carrying: "Who's carrying what",
  needsHand: NEEDS_A_HAND,
  justThis: "Just this one",
  everyTime: "Every time",
  unassigned: "Anyone on shift",
  today: "today",
  stepInDone: "Thanks for stepping in. It's recorded.",
  removed: "Back to anyone on shift.",
} as const;

/** The sentence after an assignment: whose list it is on (the same words as a hand-off, handoff.ts handoffLine). */
export function assignedLine(target: AssignTarget, name: string | undefined, scope: AssignScope): string {
  if (target.kind === "role") return `${roleLabel(target.role).label} has it ${scope === "standing" ? "every time" : "today"}. Tell them in person too.`;
  return `It's on ${firstName(name ?? "them")}'s list${scope === "standing" ? " every time" : ""}. Tell them in person too.`;
}

/* ─────────────── one request, planned (the API and both demos run the same plan) ─────────────── */

/** A notice to write: who, what kind, the words. `refId` "check:<key>" opens the task like a Nag does. */
export interface PlannedNotice { to: { employeeId: Id; name: string }[]; refId?: string; title: string; body: string }

export interface AssignPlanInput {
  req: { itemKey: string; scope: AssignScope; to: AssignTarget | null };
  actor: AssignActor;
  people: readonly ShiftPerson[];
  /** Today's expected checks. */
  expected: readonly { key: string; label: string; satisfied?: boolean }[];
  events: readonly TaskAssignment[];
  now: IsoDateTime;
  date: IsoDate;
}

export type AssignPlan =
  | {
    ok: true;
    event: Omit<TaskAssignment, "id" | "orgId" | "locationId">;
    notices: PlannedNotice[];
    /** What the audit trail gets: the action and the diff (never anything typed). */
    audit: { action: "task.assign" | "task.remove"; entityId: Id; diff: Record<string, { from: unknown; to: unknown }> };
    /** The answer, as `viewerId` reads it. */
    response: (viewerId: Id | undefined) => AssignResponse;
  }
  | { ok: false; status: number; error: string };

/** A2, A4, A8: assigning (or taking back) one task. Pure: no store, no clock. */
export function planAssign(i: AssignPlanInput): AssignPlan {
  const { req, actor } = i;
  if (!mayAssign(actor.role, actor.isPersonInCharge)) return { ok: false, status: 403, error: ASSIGN_DENIED };
  const item = i.expected.find((e) => (req.scope === "standing" ? baseKey(e.key) === baseKey(req.itemKey) : e.key === req.itemKey));
  if (!item) return { ok: false, status: 400, error: "That task isn't on today's list." };
  const before = currentAssignment(i.events, req.itemKey, i.date);
  const r = assignEvent(req.to === null ? { itemKey: req.itemKey, itemTitle: item.label, to: null, scope: req.scope } : { itemKey: req.itemKey, itemTitle: item.label, to: req.to, scope: req.scope }, actor, i.people, nextAt(i.events, req.itemKey, i.now), i.date);
  if (!r.ok) return r;
  const nameOf = (id?: Id) => i.people.find((p) => p.id === id)?.name;
  const wasName = before ? (before.target.kind === "person" ? nameOf(before.target.employeeId) : before.target.role) : undefined;
  const to = req.to;
  const diff: Record<string, { from: unknown; to: unknown }> = {
    item: { from: null, to: item.label }, scope: { from: null, to: req.scope },
    ...(to?.kind === "person" ? { employeeId: { from: null, to: to.employeeId } } : {}), ...(to?.kind === "role" ? { role: { from: null, to: to.role } } : {}),
    ...(wasName ? { was: { from: wasName, to: null } } : {}),
  };
  // The people it is now on are told, never the person who asked.
  const notices: PlannedNotice[] = [];
  if (to) {
    const resolved = resolveTarget(to, i.people).filter((p) => p.id !== actor.employeeId);
    if (resolved.length) {
      const w = assignedNoticeText({ byName: actor.name, itemTitle: item.label, target: to, scope: req.scope });
      notices.push({ to: resolved.map((p) => ({ employeeId: p.id, name: p.name })), refId: `check:${req.itemKey}`, title: w.title, body: w.body });
    }
  }
  const event = r.event;
  return {
    ok: true, event, notices,
    audit: { action: to === null ? "task.remove" : "task.assign", entityId: to?.kind === "person" ? to.employeeId : actor.employeeId ?? "", diff },
    response: (viewerId) => {
      const stored = { id: "~", orgId: "", locationId: "", ...event } as TaskAssignment;
      const view = to ? assignViewOf(currentAssignment([...i.events, stored], req.itemKey, i.date), i.people, viewerId) : undefined;
      return { assign: view ?? null, line: to === null ? DELEGATE_WORDS.removed : assignedLine(to, to.kind === "person" ? nameOf(to.employeeId) : undefined, req.scope), notified: notices.reduce((n, x) => n + x.to.length, 0) };
    },
  };
}

export interface StepInPlanInput {
  itemKey: string;
  by: { employeeId: Id; name: string };
  people: readonly ShiftPerson[];
  expected: readonly { key: string; label: string; satisfied?: boolean }[];
  events: readonly TaskAssignment[];
  now: IsoDateTime;
  date: IsoDate;
}

export type StepInPlan =
  | { ok: true; recorded: false; response: StepInResponse }
  | {
    ok: true; recorded: true;
    event: Omit<TaskAssignment, "id" | "orgId" | "locationId">;
    notices: PlannedNotice[];
    audit: { action: "task.step_in"; entityId: Id; diff: Record<string, { from: unknown; to: unknown }> };
    response: StepInResponse;
  }
  | { ok: false; status: number; error: string };

/** A6: "I'll do it". Pure. A second tap, or a task that is already mine, records nothing. */
export function planStepIn(i: StepInPlanInput): StepInPlan {
  const item = i.expected.find((e) => e.key === i.itemKey);
  if (!item) return { ok: false, status: 400, error: "That task isn't on today's list." };
  if (item.satisfied) return { ok: false, status: 400, error: "That one is already done." };
  const cur = currentAssignment(i.events, i.itemKey, i.date);
  if (cur?.target.kind === "person" && cur.target.employeeId === i.by.employeeId) return { ok: true, recorded: false, response: { assign: assignViewOf(cur, i.people, i.by.employeeId) ?? null, line: DELEGATE_WORDS.stepInDone, recorded: false } };
  const r = stepInEvent({ itemKey: i.itemKey, itemTitle: item.label, by: i.by, ...(cur ? { current: cur } : {}), people: i.people }, nextAt(i.events, i.itemKey, i.now), i.date);
  if (!r.ok) return r;
  const event = r.event;
  const notices: PlannedNotice[] = [];
  if (event.forEmployeeId) { const w = stepInNoticeText({ byName: i.by.name, itemTitle: item.label }); notices.push({ to: [{ employeeId: event.forEmployeeId, name: "" }], title: w.title, body: w.body }); }
  const stored = { id: "~", orgId: "", locationId: "", ...event } as TaskAssignment;
  return {
    ok: true, recorded: true, event, notices,
    audit: { action: "task.step_in", entityId: i.by.employeeId, diff: { item: { from: null, to: item.label }, ...(event.forEmployeeId ? { forEmployeeId: { from: null, to: event.forEmployeeId } } : {}) } },
    response: { assign: assignViewOf(currentAssignment([...i.events, stored], i.itemKey, i.date), i.people, i.by.employeeId) ?? null, line: DELEGATE_WORDS.stepInDone, recorded: true },
  };
}

/* ─────────────── what the routes answer (one shape for the API, the web and the phone) ─────────────── */

/** GET /orgs/:orgId/carrying (manager+ or the person in charge). */
export type CarryingResponse = CarryView & { locationId: Id };
/** POST /orgs/:orgId/assignments. `to: null` takes it back to anyone on shift. */
export interface AssignBody { locationId: Id; itemKey: string; to: { employeeId: Id } | { role: string } | null; scope: AssignScope }
/** `assign`: the item as the caller reads it now (null once taken back). `line`: whose list it is on (the hand-off words). `notified`: how many people were told. */
export interface AssignResponse { assign: AssignView | null; line: string; notified: number }
/** POST /orgs/:orgId/assignments/step-in. */
export interface StepInBody { locationId: Id; itemKey: string }
/** `recorded` is false on a second tap: nothing new was written. */
export interface StepInResponse { assign: AssignView | null; line: string; recorded: boolean }
